import React, { useEffect } from 'react';
import './Whyus.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { 
    FiZap, FiUsers, FiTrendingUp, FiLayers, FiShield, FiHeart, 
    FiBarChart2, FiCheckCircle, FiAward, FiMessageSquare, FiThumbsUp, FiCpu, FiArrowRight
} from 'react-icons/fi';

// import Navbar from './Navbar';
// import Footer from './Footer';

const WhyUsPage = () => {
  useEffect(() => {
    AOS.init({
      duration: 1200, // Slightly longer for smoother effects
      once: true, // Keep true for content pages unless continuous animation is desired
      mirror: false,
      offset: 100, // Trigger animations a bit later for a more deliberate reveal
      easing: 'cubic-bezier(0.16, 1, 0.3, 1)', // Spring-like ease for impact
    });
    window.scrollTo(0, 0);
  }, []);

  const coreServices = [
    { title: "Strategic Consultancy", icon: <FiCpu />, points: ["Workforce Planning & Analytics", "Organizational Design & Dev.", "Operational Excellence", "Digital Transformation Advisory"] },
    { title: "Manpower Solutions", icon: <FiUsers />, points: ["Agile Talent Acquisition", "Executive Search & Leadership", "Niche & Skilled Labor Sourcing", "Global Workforce Mobility"] },
    { title: "HR & Talent Development", icon: <FiAward />, points: ["Future-Ready Skill Academies", "Immersive Onboarding Experiences", "AI-Powered Performance Systems", "Transformational Leadership Coaching"] },
  ];

  const whyChooseUsPoints = [
    { icon: <FiHeart />, title: "Deep Client Partnership", text: "We immerse ourselves in your vision, co-creating strategies for unparalleled value." },
    { icon: <FiLayers />, title: "Multifaceted Industry Mastery", text: "Our collective intelligence spans diverse sectors, delivering nuanced expertise." },
    { icon: <FiZap />, title: "Insight-Driven Execution", text: "Leveraging advanced analytics & AI for intelligent manpower and advisory." },
    { icon: <FiShield />, title: "Unwavering Integrity & Transparency", text: "Every interaction is anchored in trust, ethical conduct, and full compliance." },
    { icon: <FiBarChart2 />, title: "Adaptive Engagement Architectures", text: "From agile sprints to comprehensive transformations, our models scale to your ambition." }
  ];

  const impactStats = [
    { value: "150+", label: "Strategic Placements", unit: "Across Sectors", aos: "impact-stat-reveal", delay: "100" },
    { value: "38%", label: "Accelerated Hiring", unit: "Avg. Time Reduction", aos: "impact-stat-reveal", delay: "250" },
    { value: "500+", label: "Empowered Professionals", unit: "Via Custom Training", aos: "impact-stat-reveal", delay: "400" },
    { value: "30%", label: "Enhanced Retention", unit: "Avg. Client Improvement", aos: "impact-stat-reveal", delay: "550" } // Simplified one stat for brevity
  ];
  
  const coreValues = [
    { title: "Integrity First", text: "Upholding the highest ethical standards, always.", icon: <FiShield />, aos: "fade-up" },
    { title: "Pursuit of Excellence", text: "Continuously raising the bar for quality and impact.", icon: <FiAward />, aos: "fade-up" },
    { title: "People Empowerment", text: "Unlocking potential in individuals and organizations.", icon: <FiUsers />, aos: "fade-up" },
    { title: "Bold Innovation", text: "Leading with creative solutions and forward-thinking.", icon: <FiZap />, aos: "fade-up" },
    { title: "Synergistic Collaboration", text: "Achieving greater results through unified partnership.", icon: <FiHeart />, aos: "fade-up" }
  ];

  return (
    <>
      {/* <Navbar /> */}
      <div className="why-us-elite-wrapper">

        {/* --- Hero Section: What We Do --- */}
        <section className="what-we-do-hero-elite">
          <div className="hero-bg-pattern-elite"></div>
          <div className="hero-bg-gradient-elite"></div>
          <div className="container hero-content-elite">
            <div className="hero-text-container-elite">
              <h1 data-aos="zoom-out-down" data-aos-duration="1000">
                <span className="headline-line">Orchestrating</span>
                <span className="headline-line highlight-elite">Your Success.</span>
              </h1>
              <p className="hero-subtext-elite" data-aos="fade-up" data-aos-duration="1000" data-aos-delay="300">
                Adhvik & Adhrit specializes in delivering transformative consultancy and end-to-end manpower solutions, empowering businesses across diverse sectors to achieve peak performance and sustainable growth.
              </p>
            </div>
          </div>
          <div className="hero-scroll-cue">
            <div className="mousey">
              <div className="scroller"></div>
            </div>
          </div>
        </section>

        {/* --- Core Services Section (Interactive Tabs/Accordion Concept) --- */}
        <section className="core-services-elite section-padding-whyus-elite">
          <div className="container">
            <h2 className="section-title-elite" data-aos="fade-up">
              Our Core <span className="highlight-elite-alt">Competencies</span>
            </h2>
            <div className="core-services-interactive-grid">
              {coreServices.map((service, index) => (
                <div className="service-capsule-elite" key={index} data-aos="fade-up" data-aos-delay={100 + index * 100}>
                  <div className="service-capsule-header">
                    <span className="service-capsule-icon">{service.icon}</span>
                    <h3 className="service-capsule-title">{service.title}</h3>
                  </div>
                  <div className="service-capsule-content">
                    <ul>
                      {service.points.map((point, pIndex) => (
                        <li key={pIndex}>
                          <FiCheckCircle className="capsule-point-icon"/> {point}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* --- Why Choose Us Section (Asymmetrical, Storytelling) --- */}
        <section className="why-choose-elite section-padding-whyus-elite bg-alt-whyus-elite">
          <div className="container">
            <h2 className="section-title-elite" data-aos="fade-up">
              The Adhvik & Adhrit <span className="highlight-elite-alt">Advantage</span>
            </h2>
            <div className="why-choose-layout-elite">
              {whyChooseUsPoints.map((item, index) => (
                <div 
                  className={`why-choose-card-elite card-position-${index + 1}`} 
                  key={index} 
                  data-aos="zoom-in-up" // Custom animation could be applied for each
                  data-aos-delay={150 + index * 100}
                >
                  <div className="why-choose-card-icon-elite">{item.icon}</div>
                  <h3 className="why-choose-card-title-elite">{item.title}</h3>
                  <p className="why-choose-card-text-elite">{item.text}</p>
                  <div className="card-accent-glow-elite"></div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* --- Our Impact Section (Dynamic Visuals) --- */}
        <section className="our-impact-elite section-padding-whyus-elite">
          <div className="container">
            <h2 className="section-title-elite" data-aos="fade-up">
              Transformative <span className="highlight-elite-alt">Impact</span>
            </h2>
            <div className="impact-showcase-elite">
              {impactStats.map((stat, index) => (
                <div className="impact-item-elite" key={index} data-aos={stat.aos} data-aos-delay={stat.delay}>
                  <div className="impact-value-elite">{stat.value}</div>
                  <div className="impact-label-elite">{stat.label}</div>
                  <div className="impact-unit-elite">{stat.unit}</div>
                  <div className="impact-item-bg-shape"></div>
                </div>
              ))}
            </div>
            <p className="impact-summary-elite" data-aos="fade-up" data-aos-delay="600">
              These milestones reflect our commitment to tangible results and client success. We've also guided over <strong>30+ startups and SMEs</strong> through pivotal organizational transformations.
            </p>
          </div>
        </section>
        
        {/* --- Core Values Section (Hexagonal or Unique Grid) --- */}
        <section className="core-values-elite section-padding-whyus-elite bg-alt-whyus-elite">
          <div className="container">
            <h2 className="section-title-elite" data-aos="fade-up">
              Our Guiding <span className="highlight-elite-alt">Principles</span>
            </h2>
            <div className="core-values-container-elite">
              {coreValues.map((value, index) => (
                <div 
                  className="value-hexagon-elite" 
                  key={index} 
                  data-aos={value.aos} 
                  data-aos-delay={100 + (index % 3) * 100} // Stagger rows
                >
                  <div className="hexagon-inner-elite">
                    <div className="hexagon-icon-elite">{value.icon}</div>
                    <h3 className="hexagon-title-elite">{value.title}</h3>
                    <p className="hexagon-text-elite">{value.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

      </div>
      
    </>
  );
};

export default WhyUsPage;